import { useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

export const ALL_SIZE = 100000;
const SIZES = [10, 20, 50, 100];

// Akıllı sayfa penceresi: 1 2 … (cur-1) cur (cur+1) … son-1 son
function pageWindow(cur, pages) {
  const want = new Set([1, 2, cur - 1, cur, cur + 1, pages - 1, pages]);
  const nums = [...want].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const res = [];
  let prev = 0;
  for (const p of nums) {
    if (prev && p - prev > 1) res.push("…");
    res.push(p);
    prev = p;
  }
  return res;
}

export function Pagination({ page, pageSize, total, onPage, onPageSize, sizes = SIZES, allowAll = false, testid = "pagination" }) {
  const [jump, setJump] = useState("");
  const isAll = pageSize >= ALL_SIZE;
  const eff = isAll ? Math.max(1, total || 1) : pageSize;
  const pages = Math.max(1, Math.ceil((total || 0) / eff));
  const cur = Math.min(page, pages);
  const from = total === 0 ? 0 : (cur - 1) * eff + 1;
  const to = isAll ? total : Math.min(total, cur * eff);
  const go = (p) => onPage(Math.min(pages, Math.max(1, p)));
  const doJump = () => { const n = parseInt(jump, 10); if (!isNaN(n)) go(n); setJump(""); };
  const win = pageWindow(cur, pages);

  return (
    <div data-testid={testid} className="flex flex-wrap items-center justify-between gap-3 pt-1">
      <p className="text-xs text-slate-400 tabular">{from}–{to} / {total} kayıt</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <span>Sayfa başına</span>
          <div className="relative">
            <select data-testid={`${testid}-size`} value={isAll ? "all" : pageSize}
              onChange={(e) => onPageSize(e.target.value === "all" ? ALL_SIZE : Number(e.target.value))}
              className="h-8 appearance-none rounded-md border border-input bg-white pl-2.5 pr-7 text-xs font-semibold tabular focus:outline-none focus:ring-2 focus:ring-brand-green">
              {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
              {allowAll && <option value="all">Hepsi</option>}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid={`${testid}-prev`} onClick={() => go(cur - 1)} disabled={cur <= 1}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white">
            <ChevronLeft className="h-4 w-4" />
          </button>
          {isAll ? (
            <span className="min-w-[52px] px-2 text-center text-xs font-medium text-slate-600">Hepsi</span>
          ) : win.map((p, i) => p === "…" ? (
            <span key={`e${i}`} className="px-1 text-xs text-slate-400">…</span>
          ) : (
            <button key={p} data-testid={`${testid}-page-${p}`} onClick={() => go(p)} aria-current={p === cur ? "page" : undefined}
              className={`flex h-8 min-w-[32px] items-center justify-center rounded-md border px-1.5 text-xs font-semibold tabular transition-colors ${p === cur ? "border-brand-green bg-brand-green text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              {p}
            </button>
          ))}
          <button data-testid={`${testid}-next`} onClick={() => go(cur + 1)} disabled={cur >= pages}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {!isAll && pages > 3 && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <input data-testid={`${testid}-jump`} value={jump}
              onChange={(e) => setJump(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") doJump(); }}
              placeholder="Sayfa" inputMode="numeric"
              className="h-8 w-16 rounded-md border border-input bg-white px-2 text-xs tabular focus:outline-none focus:ring-2 focus:ring-brand-green" />
            <button data-testid={`${testid}-jump-go`} onClick={doJump}
              className="h-8 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Git</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Pagination;
